import { Injectable } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from '../store/app.state';
import { AuthenticationService } from '../authentication/authentication.service';
import { SocketMainService } from '../socket/socket-main.service';
import { SocketEvents, SocketModels } from '../socket/socket.model';
import { SharedActions } from '@states/shared/shared.action-types';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { ActiveOrganization } from '@models/organization.model';
import * as OrganizationSelectors from '@states/organization/organization.selectors';
import { Archive } from '@models/archive.model';
import { ArchiveActions } from '@states/archive/archive.action-types';
import { CamerasActions } from '@states/cameras/cameras.action-types';
import { AppUser } from '../user/user.model';
import { UserSelectors } from '@states/user/user.selector-types';
import * as SearchConfigurationActions from '@states/camera-edit/camera-edit.actions';
import * as EdgeEditActions from '@states/edge-edit/edge-edit.actions';
import { EdgeActions } from '@states/edge/edge.action-types';
import { AppleTvActions } from '@states/apple-tv/apple-tv.action-types';
import { Edge } from '../edge/edge.model';
import { AvailableDevicesSelectors } from '@states/available-devices/available-devices.selector-types';
import { Operator } from '../operator/operator.model';
import { AvailableDevicesActions } from '@states/available-devices/available-devices.action-type';

@Injectable({ providedIn: 'root' })
export class SocketBootstrapService {
  private initialized = false;

  private readonly selectActiveOrganization$: Observable<ActiveOrganization> = this.store$.pipe(
    select(OrganizationSelectors.selectActiveOrganization),
  );
  private readonly selectUser$: Observable<AppUser.User> = this.store$.select(UserSelectors.selectUser);
  private readonly selectAvailableDevices$: Observable<Operator.AvailableDeviceDocument[]> = this.store$.pipe(
    select(AvailableDevicesSelectors.selectAvailableDevices),
  );

  constructor(
    private readonly store$: Store<AppState>,
    private readonly authenticationService: AuthenticationService,
    private readonly socketMainService: SocketMainService,
  ) {}

  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.socketMainService.consume<void>(SocketEvents.managementForceLogoutMessage)
      .subscribe(() => {
        this.store$.dispatch(AuthenticationActions.Logout({ params: { msg: 'Socket managementForceLogoutMessage' } }));
      });

    this.socketMainService.consume<{
      data: Archive.ArchiveDocument,
      error: string,
      statusCode: HttpStatusCode
    }>(SocketEvents.edgeArchiveProgressNotification)
      .subscribe((res) => {
        this.store$.dispatch(ArchiveActions.archiveProgressSocketUpdate({
          archive: res.data,
          error: res.error,
          statusCode: res.statusCode,
        }));
      });

    this.socketMainService.consume<SocketModels.DeleteOrganizationExternalResponse>(SocketEvents.deleteOrgNotification)
      .subscribe((res) => {
        this.selectActiveOrganization$.pipe(take(1))
          .subscribe(activeOrg => {
            if (activeOrg.orgId === res.organizationId) {
              this.authenticationService.logout();
            }
          });
      });

    this.socketMainService.consume<SocketModels.CreateCameraExternalResponse>(SocketEvents.cameraCreatedNotification)
      .pipe(
        withLatestFrom(this.selectUser$),
        tap(([{ userId, warnLevel, orgMessage, camera, showToast }, user]) => {
          this.store$.dispatch(CamerasActions.sendCreateCameraSuccess({ camera }));
          if (userId !== user._id && warnLevel !== SocketModels.MessageLevel.ERROR && (showToast == undefined || showToast)) {
            this.store$.dispatch(SharedActions.showMessageFromSocket({
              level: warnLevel,
              msg: orgMessage,
            }));
          }
        }),
      )
      .subscribe();

    this.socketMainService.consume<SocketModels.UpdateCameraExternalResponse>(SocketEvents.cameraUpdatedNotification)
      .pipe(
        withLatestFrom(this.selectUser$),
        tap(([{ userId, warnLevel, orgMessage, location, cameraId, edgeId, showToast }, user]) => {
          if (userId !== user._id) {
            this.store$.dispatch(SearchConfigurationActions.sendCameraDetailsUpdateSuccess({
              location,
              cameraId,
              edgeId,
            }));
            if (warnLevel !== SocketModels.MessageLevel.ERROR && (showToast == undefined || showToast)) {
              this.store$.dispatch(SharedActions.showMessageFromSocket({
                level: warnLevel,
                msg: orgMessage,
              }));
            }
          }
        }),
      )
      .subscribe();

    this.socketMainService.consume<SocketModels.DeleteCameraExternalResponse>(SocketEvents.cameraDeletedNotification)
      .pipe(
        withLatestFrom(this.selectUser$),
        tap(([{ userId, warnLevel, orgMessage, locationId, edgeId, cameraId, showToast }, user]) => {
          if (userId !== user._id) {
            this.store$.dispatch(EdgeActions.callDeleteCameraFromLocationSuccess({ locationId, edgeId, cameraId }));
            if (warnLevel !== SocketModels.MessageLevel.ERROR && (showToast == undefined || showToast)) {
              this.store$.dispatch(SharedActions.showMessageFromSocket({
                level: warnLevel,
                msg: orgMessage,
              }));
            }
          }
        }),
      )
      .subscribe();

    this.socketMainService.consume<SocketModels.UpdateEdgeSwExternalResponse>(SocketEvents.edgeSwUpdateNotification)
      .subscribe();

    this.socketMainService.consume<SocketModels.CreateEdgeExternalResponse>(SocketEvents.edgeCreatedNotification)
      .pipe(
        withLatestFrom(this.selectUser$, this.selectAvailableDevices$),
        tap(([event, user, devices]) => {
          const shouldRemove = devices.some(device => device._id === event.edgeId);
          if (shouldRemove) {
            this.store$.dispatch(AvailableDevicesActions.removeAvailableDevice({ deviceId: event.edgeId }));
          }

          if (event.userId !== user._id) {
            const edge = event.location.edges[event.edgeId] as Edge.V2.EdgeCreateResponse;

            this.store$.dispatch(EdgeActions.createEdgeServerRequestSuccess({ edge }));

            if (event.warnLevel !== SocketModels.MessageLevel.ERROR && (event.showToast === undefined || event.showToast)) {
              this.store$.dispatch(SharedActions.showMessageFromSocket({
                level: event.warnLevel,
                msg: event.orgMessage,
              }));
            }
          }
        }),
      )
      .subscribe();

    this.socketMainService.consume<SocketModels.UpdateEdgeExternalResponse>(SocketEvents.edgeUpdatedNotification)
      .pipe(
        withLatestFrom(this.selectUser$),
        tap(([{ userId, warnLevel, orgMessage, location, edgeId, showToast }, user]) => {
          if (userId !== user._id) {
            this.store$.dispatch(EdgeEditActions.updateEdgeInLocationSuccessApiAndSocket({
              updatedLocation: location,
              edgeId,
            }));
            if (warnLevel !== SocketModels.MessageLevel.ERROR && (showToast == undefined || showToast)) {
              this.store$.dispatch(SharedActions.showMessageFromSocket({
                level: warnLevel,
                msg: orgMessage,
              }));
            }
          }
        }),
      )
      .subscribe();

    this.socketMainService.consume<SocketModels.DeleteEdgeExternalResponse>(SocketEvents.edgeDeletedNotification)
      .pipe(
        withLatestFrom(this.selectUser$),
        tap(([{ userId, warnLevel, orgMessage, location, edgeId, showToast }, user]) => {
          if (userId !== user._id) {
            this.store$.dispatch(EdgeActions.deleteEdgeServerRequestSuccessSocketAndApi({ location, edgeId }));
            if (warnLevel !== SocketModels.MessageLevel.ERROR && (showToast == undefined || showToast)) {
              this.store$.dispatch(SharedActions.showMessageFromSocket({
                level: warnLevel,
                msg: orgMessage,
              }));
            }
          }
        }),
      )
      .subscribe();

    this.socketMainService.consume<any>(SocketEvents.cameraRebootNotification)
      .subscribe((res) => {
        console.log('SocketEvents.cameraRebootNotification', res);
      });

    this.socketMainService.consume<SocketModels.OnSwUpdateExternalResponse>(SocketEvents.onSwUpdateNotification)
      .subscribe();

    this.socketMainService.consume<SocketModels.CreateAppleTv.Response>(SocketEvents.appletvCreatedNotification)
      .subscribe((res) => {
        switch (res.state) {
          case 'COMPLETED':
            this.store$.dispatch(AppleTvActions.onSocketCreatedSuccess({
              document: res.data,
              createdByEmail: res?.email,
              authProviderId: res.authProviderId,
            }));
            break;
          case 'ERROR':
            this.store$.dispatch(AppleTvActions.onSocketCreatedError({
              error: res.error,
              authProviderId: res.authProviderId,
            }));
            break;
        }
      });

    this.socketMainService.consume<SocketModels.DeleteAppleTv.Response>(SocketEvents.appletvDeletedNotification)
      .subscribe((res) => {
        switch (res.state) {
          case 'COMPLETED':
            this.store$.dispatch(AppleTvActions.onSocketDeletedSuccess({
              document: res.data,
              authProviderId: res.authProviderId,
              email: res?.email,
            }));
            break;
          case 'ERROR':
            this.store$.dispatch(AppleTvActions.onSocketDeletedFail({ error: res.error }));
            break;
        }
      });

    this.socketMainService.consume<SocketModels.ConfigureAppleTv.Response>(SocketEvents.appletvConfigureWallNotification)
      .subscribe((res) => {
        switch (res.state) {
          case 'COMPLETED':
            this.store$.dispatch(AppleTvActions.onSocketConfigureSuccess({
              response: res.data,
              configuredBy: res?.email,
              authProviderId: res.authProviderId,
            }));
            break;
          case 'ERROR':
            this.store$.dispatch(AppleTvActions.onSocketConfigureFail({
              error: res.error,
              authProviderId: res.authProviderId,
              edgeId: res.edgeId,
            }));
            break;
        }
      });
  }
}
